import {Carousel} from '@mantine/carousel';
import {Image, Loader} from '@mantine/core';
import {BasicButton} from '@packages/ui';
import {useEffect, useState} from 'react';
import type {
  ColumnCarousel,
  PostListSchema,
} from '../../../../../core/entities/columns/schema';
import {
  useGetColumnCarouselList,
  useGetPostList,
} from '../../../../../core/repositories/columnRepository';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {formatDate} from '../../../../../shared/services/formatDate';
import {getMailAuthLinkWithParamsHooks} from '../../../../../shared/services/getMailAuthLinkWithParamsHooks';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {isNewPost} from '../../../../columns/usecases/isNewPost';
import {validateCarousel} from '../../../usecase/validateCarousel';

export const NoLoggedInColumnList: React.FC = () => {
  const {isPC} = useMediaQuery();
  const [initialized, setInitialized] = useState(false);
  const [validatedCarousel, setValidatedCarousel] = useState<ColumnCarousel[]>(
    [],
  );

  const {data: carouselData, isLoading: isCarouselLoading} =
    useGetColumnCarouselList({});

  const {data: resentPostData, isLoading: isResentPostLoading} = useGetPostList(
    {queryParams: {limit: 6}},
  );

  useEffect(() => {
    const validateImages = async () => {
      if (!carouselData?.ok) return;

      const validatedImages = await validateCarousel(
        carouselData.value.carousel,
      );
      setValidatedCarousel(validatedImages);
      setInitialized(true);
    };

    void validateImages();
  }, [carouselData]);

  return (
    <div className="w-full pc:w-[1200px] bg-white pc:p-32 pc:mt-16 flex flex-col pc:flex-row pc:gap-16 justify-between items-stretch">
      <div className="w-full pc:w-1/2">
        {isCarouselLoading && <LoadingComponent />}
        {carouselData?.ok &&
        !isCarouselLoading &&
        initialized &&
        validatedCarousel.length > 0 ? (
          <Carousel slideSize={'100%'} align={'start'} loop withIndicators>
            {validatedCarousel
              .filter((c) => c.imageURL)
              .map((carousel, index) => (
                <Carousel.Slide key={carousel.id}>
                  <CommonLink
                    href={`/archives/${carousel.id}`}
                    className="relative overflow-hidden flex"
                  >
                    <Image
                      src={carousel.imageURL ?? ''}
                      alt={`carousel-${index}`}
                      fit="cover"
                    />
                    <div className="absolute w-full bottom-0 bg-black bg-opacity-70 p-16">
                      <p className="text-md text-white font-bold text-wrap work-wrap line-clamp-2">
                        {carousel.title}
                      </p>
                    </div>
                  </CommonLink>
                </Carousel.Slide>
              ))}
          </Carousel>
        ) : null}
      </div>
      <div className="w-full pc:w-1/2 flex flex-col justify-between">
        {resentPostData?.ok &&
        !isResentPostLoading &&
        resentPostData.value.posts.length > 0 ? (
          <div className="flex justify-between flex-wrap">
            {resentPostData.value.posts
              .slice(0, isPC ? 2 : 6)
              .map((post, index) =>
                isPC ? (
                  <ResentPostComponentForPc
                    key={post.id}
                    post={post}
                    index={index}
                  />
                ) : (
                  <RecentPostComponentForSp
                    key={post.id}
                    post={post}
                    index={index}
                  />
                ),
              )}
          </div>
        ) : (
          <LoadingComponent />
        )}
        <RegisterNavigationButton />
      </div>
    </div>
  );
};

const ResentPostComponentForPc: React.FC<{
  post: PostListSchema;
  index: number;
}> = ({post, index}) => {
  return (
    <CommonLink
      key={`resent-post-${index}`}
      href={`archives/${post.id}`}
      className="block relative w-[calc(50%-8px)] [&:nth-child(n+3)]:mt-16 pc:hover:opacity-80"
    >
      <div className="w-full aspect-[244/155] overflow-hidden relative">
        <Image
          src={post.carouselThumbnailURL ?? ''}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        {isNewPost({
          postDate: post.updateDateTime,
          currentDate: String(new Date()),
        }) && (
          <div className="absolute top-[5px] right-[-20px] bg-error py-[1px] px-[30px] origin-center rotate-[30deg]">
            <p className="text-white text-xs">NEW!!</p>
          </div>
        )}
      </div>
      <p className="bg-white opacity-80 text-black-1 text-sm p-4 absolute top-0 left-0">
        {post.tags[0]}
      </p>
      <div className="bg-black opacity-80 p-8 absolute bottom-0 left-0 right-0">
        <p className=" text-white text-base leading line-clamp-2 overflow-hidden">
          {post.title}
        </p>
      </div>
    </CommonLink>
  );
};

const RecentPostComponentForSp: React.FC<{
  post: PostListSchema;
  index: number;
}> = ({post, index}) => {
  return (
    <CommonLink
      key={`resent-post-${index}`}
      href={`archives/${post.id}`}
      className="w-full flex justify-between items-center p-12 border-b-[1px] border-line border-collapse [&:nth-last-child(1)]:border-b-0"
    >
      <div className="w-[calc(100%-85px)] h-[85px] pr-12 flex flex-col justify-between">
        <p className="text-black-1 text-base leading line-clamp-3 overflow-hidden">
          {post.title}
        </p>
        <p className="text-sm text-black-2 leading">
          {formatDate(post.publishDateTime)}
        </p>
      </div>
      <div className="w-[85px] h-[85px] overflow-hidden">
        <Image
          src={post.thumbnailURL ?? ''}
          alt={post.title}
          width={85}
          height={85}
          className="object-cover"
        />
      </div>
    </CommonLink>
  );
};

const RegisterNavigationButton: React.FC = () => {
  const mailAuthLink = getMailAuthLinkWithParamsHooks({});

  return (
    <div className="mt-16 px-12 pc:px-0">
      <BasicButton
        label="無料会員登録してもっと見る"
        isPrimary={true}
        isLarge={true}
        onClick={() => {
          location.href = mailAuthLink;
        }}
        className="w-full"
      />
    </div>
  );
};

const LoadingComponent: React.FC = () => (
  <div className="flex justify-center py-24">
    <Loader color="gray" size="lg" />
  </div>
);
